import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { GlassCard, GradientButton, LoadingOverlay, useAlert } from '../../components';
import { getUserProducts, claimDailyIncome } from '../../services/firestoreService';
import { formatCurrency, formatDate, getDaysRemaining, canClaimToday } from '../../utils/helpers';
import './MyProductsScreen.css';

export const MyProductsScreen = () => {
    const navigate = useNavigate();
    const { refreshUserData } = useAuth();
    const alert = useAlert();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [claiming, setClaiming] = useState(null);

    useEffect(() => {
        loadProducts();
    }, []);

    const loadProducts = async () => {
        try {
            const data = await getUserProducts();
            setProducts(data);
        } catch (error) {
            alert.showError('Error', 'Failed to load products');
        } finally {
            setLoading(false);
        }
    };

    const handleClaimIncome = async (product) => {
        if (!canClaimToday(product.lastClaimDate)) {
            alert.showInfo('Already Claimed', 'You have already claimed income for today. Come back tomorrow!');
            return;
        }

        const daysRemaining = getDaysRemaining(product.expiryDate);
        if (daysRemaining <= 0) {
            alert.showInfo('Expired', 'This product has expired.');
            return;
        }

        setClaiming(product.id);
        try {
            await claimDailyIncome(product);
            await refreshUserData();
            await loadProducts();
            alert.showSuccess('Success', `Successfully claimed ${formatCurrency(product.dailyIncome)}!`);
        } catch (error) {
            alert.showError('Error', error.message || 'Failed to claim income');
        } finally {
            setClaiming(null);
        }
    };

    const calculateProgress = (product) => {
        const totalPotential = (product.dailyIncome || 1) * (product.validity || 1);
        const claimed = product.totalClaimed || 0;
        return Math.min(100, (claimed / totalPotential) * 100);
    };

    if (loading) {
        return <LoadingOverlay visible message="Loading your products..." />;
    }

    return (
        <div className="my-products-screen">
            <div className="screen-header">
                <button className="back-btn" onClick={() => navigate(-1)}>
                    <span className="material-icons">arrow_back</span>
                </button>
                <h1 className="screen-title">My Products</h1>
                <div style={{ width: 40 }}></div>
            </div>

            {products.length > 0 ? (
                <div className="products-list">
                    {products.map((product) => {
                        const daysRemaining = getDaysRemaining(product.expiryDate);
                        const canClaim = canClaimToday(product.lastClaimDate) && daysRemaining > 0;
                        const progress = calculateProgress(product);

                        return (
                            <GlassCard key={product.id} className="product-card">
                                <div className="product-header">
                                    <div className="product-icon">
                                        <span className="material-icons">memory</span>
                                    </div>
                                    <div className="product-title">
                                        <h3 className="product-name">{product.productName}</h3>
                                        <span className={`status-badge ${daysRemaining > 0 ? 'active' : 'expired'}`}>
                                            {daysRemaining > 0 ? 'Active' : 'Expired'}
                                        </span>
                                    </div>
                                </div>

                                <div className="stats-grid">
                                    <div className="stat-item">
                                        <span className="stat-label">Daily Income</span>
                                        <span className="stat-value income">{formatCurrency(product.dailyIncome)}</span>
                                    </div>
                                    <div className="stat-item">
                                        <span className="stat-label">Days Remaining</span>
                                        <span className="stat-value">{daysRemaining}</span>
                                    </div>
                                    <div className="stat-item">
                                        <span className="stat-label">Total Claimed</span>
                                        <span className="stat-value">{formatCurrency(product.totalClaimed || 0)}</span>
                                    </div>
                                    <div className="stat-item">
                                        <span className="stat-label">Expires On</span>
                                        <span className="stat-value">{formatDate(product.expiryDate)}</span>
                                    </div>
                                </div>

                                <div className="progress-section">
                                    <div className="progress-header">
                                        <span>Progress</span>
                                        <span>{progress.toFixed(0)}%</span>
                                    </div>
                                    <div className="progress-bar">
                                        <div
                                            className="progress-fill"
                                            style={{ width: `${progress}%` }}
                                        ></div>
                                    </div>
                                </div>

                                <GradientButton
                                    title={canClaim ? 'Claim Daily Income' : (daysRemaining > 0 ? 'Claimed Today' : 'Expired')}
                                    onClick={() => handleClaimIncome(product)}
                                    loading={claiming === product.id}
                                    disabled={!canClaim}
                                    variant={canClaim ? 'success' : 'outline'}
                                    size="small"
                                />
                            </GlassCard>
                        );
                    })}
                </div>
            ) : (
                <div className="empty-state">
                    <span className="material-icons empty-icon">inventory_2</span>
                    <h3>No Products Yet</h3>
                    <p>Purchase a mining node to start earning daily income</p>
                    <GradientButton
                        title="Browse Products"
                        onClick={() => navigate('/products')}
                        size="small"
                    />
                </div>
            )}
        </div>
    );
};

export default MyProductsScreen;
